import { useEffect, useId } from "react";
import { ChevronDown } from "lucide-react";

export interface FaqItem {
  q: string;
  a: string;
}

interface Props {
  items: FaqItem[];
  title?: string;
  /** id único para o schema, evita conflito quando há mais de um FAQ na página */
  schemaId?: string;
  className?: string;
}

/**
 * Seção de Perguntas Frequentes que renderiza o conteúdo visível (bom para o
 * usuário e para featured snippets) e injeta schema.org FAQPage (Google + IAs).
 */
export function FaqSection({ items, title = "Perguntas frequentes", schemaId, className }: Props) {
  const reactId = useId();
  const id = schemaId || `faq-${reactId.replace(/[:]/g, "")}`;

  useEffect(() => {
    if (!items.length) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = `schema-${id}`;
    el.textContent = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => { document.getElementById(`schema-${id}`)?.remove(); };
  }, [items, id]);

  if (!items.length) return null;

  return (
    <section className={`container py-12 ${className ?? ""}`} aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`} className="font-heading font-bold text-2xl text-foreground text-center mb-8">
        {title}
      </h2>
      <div className="max-w-3xl mx-auto space-y-3">
        {items.map((it, i) => (
          <details
            key={i}
            className="group bg-white border border-gray-200 rounded-xl overflow-hidden [&[open]]:shadow-sm"
          >
            <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-5 py-4 font-heading font-semibold text-foreground hover:text-accent transition-colors">
              {it.q}
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden="true" />
            </summary>
            <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed">
              {it.a}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}
